import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import s from './TourList.module.scss';
import Pagination from '../../components/Pagination/Pagination';
import StarRating from '../../components/StarRating/StarRating';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import Loading from '../../components/Loading/Loading';
import TourCardList from '../../components/TourCardList/TourCardList';
import { AiOutlineClose } from 'react-icons/ai';
import { Helmet } from 'react-helmet';

const TourList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const apiUrl = process.env.REACT_APP_API_URL;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(parseInt(queryParams.get("page")) || 1);
  const [toursPerPage] = useState(6);
  const [totalPages, setTotalPages] = useState(1);
  const [headerTags, setHeaderTags] = useState([]);
  const [total, setTotal] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [isFirstRender, setIsFirstRender] = useState(true);


  const queryKeyword = queryParams.get('kw');
  const orderBy = queryParams.get('orderBy');
  const tags = queryParams.get('tags');
  const category = queryParams.get('category');


  useEffect(() => {
    if (tags) {
      setHeaderTags(tags.split(','));
    }
    if (queryKeyword) {
      setKeyword(queryKeyword);
    }
  }, [tags, queryKeyword]);


  const breadcrumbItems = location.state?.breadcrumbItems || [
    { name: 'Home', link: '/' },
    { name: 'ツアー一覧', link: '/all-tours' }
  ];

  const thirdBreadcrumbName = location.state?.breadcrumbItems?.[2]?.name.replace(/"/g, '');
  const metaTitle = thirdBreadcrumbName
    ? `${thirdBreadcrumbName} – ケアンズの旅行やツアーを最安値で予約【ジェイさんツアー】`
    : 'ケアンズの旅行やツアーを最安値で予約【ジェイさんツアー】';

  useEffect(() => {
    const pageFromUrl = parseInt(queryParams.get("page")) || 1;
    setCurrentPage(pageFromUrl);
  }, [location.search]);


  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set('page', newPage);
    navigate({ search: newSearchParams.toString() }, { replace: true });
  };

  const fetchTours = async () => {
    setLoading(true);
    try {
      let response;
      const queryParams = new URLSearchParams(location.search);

      const tagParam = headerTags.join(',');
      if (tagParam) queryParams.set('tags', tagParam);
      if (keyword) queryParams.set('kw', keyword);
      if (category) queryParams.set('category', category);
      if (orderBy) queryParams.set('orderBy', orderBy);

      queryParams.set('page', currentPage);
      const locationParam = queryParams.get('location');
      const saleParam = queryParams.get('sale');

      let url;

      if (saleParam === 'true') {
        url = `${apiUrl}/api/search/sale?${queryParams.toString()}`;
      } else if (locationParam) {
        url = `${apiUrl}/api/search/location?locationId=${locationParam}&${queryParams.toString()}`;
      } else {
        url = `${apiUrl}/api/search?${queryParams.toString()}`;
      }

      response = await fetch(url);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const result = await response.json();

      setData(result.data);
      setCurrentPage(result.current_page);
      setTotalPages(result.last_page);
      setTotal(result.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    fetchTours();
  }, [keyword, headerTags, orderBy, category, apiUrl, currentPage]);

  const handleCardClick = (rezdyId) => {
    navigate(`/tours/${rezdyId}`);
  };


  const handleTagDelete = (tag) => {
    setHeaderTags((prevTags) => {
      const newTags = prevTags.filter((t) => t !== tag);
      const newSearchParams = new URLSearchParams(location.search);
      if (newTags.length > 0) {
        newSearchParams.set('tags', newTags.join(','));
      } else {
        newSearchParams.delete('tags');
      }
      navigate({ search: newSearchParams.toString() });
      return newTags;
    });
  };

  const handleTagAdd = useCallback((tag) => {
    if (!headerTags.includes(tag)) {
      setHeaderTags((prevTags) => {
        const newTags = [...prevTags, tag];
        const newSearchParams = new URLSearchParams(location.search);
        newSearchParams.set('tags', newTags.join(','));
        navigate({ search: newSearchParams.toString() });
        return newTags;
      });
    }
  }, [headerTags, location.search]);

  const handleKeywordChange = (e) => {
    const newKeyword = e.target.value;
    setKeyword(newKeyword);
    const newSearchParams = new URLSearchParams(location.search);
    if (newKeyword) {
      newSearchParams.set('kw', newKeyword);
    } else {
      newSearchParams.delete('kw');
    }
    navigate({ search: newSearchParams.toString() });
  };

  if (loading) {
    return (
      <div className={s.loadingContainer}>
        <Loading loading={loading} />
      </div>
    );
  }

  if (error) return <div>Error: {error}</div>;

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta property="og:title" content={metaTitle} />
        <meta property="og:url" content={`${process.env.REACT_APP_URL}/all-tours`} />
        <meta name="description" content="自社ツアーやオプショナルツアーも予約できます！" />
        <meta property="og:description" content="自社ツアーやオプショナルツアーも予約できます！" />
        <meta property="og:type" content="article" />
      </Helmet>
      <div className={s.root}>
        <Breadcrumb items={breadcrumbItems} />
        <div className={s.body}>
          <div className={s.titleContainer}>
          <div>
            <span>All Tours</span>
            <h2>すべてのツアー</h2>
          </div>
        </div>
        {headerTags.length > 0 && (
          <div className={s.headerTags}>
            {headerTags.map((tag, index) => (
              <div key={index} className={s.headerTag}>
                <span>{tag}</span>
                <AiOutlineClose
                  className={s.removeIcon}
                  onClick={() => handleTagDelete(tag)}
                />
              </div>
            ))}
          </div>
          )}
        <TourCardList items={data} />
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </div>
      </div>
    </>
  );
};

export default TourList;
